import { useEffect, useState, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import styles from './EmailVerify.module.css';

const VerifyEmail = () => {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation('login');
  
  const [status, setStatus] = useState<'loading' | 'success' | 'error'>('loading');
  const [message, setMessage] = useState('');
  const [countdown, setCountdown] = useState(3);
  
  // Prevent double execution in React StrictMode (development)
  const hasVerified = useRef(false);

  useEffect(() => {
    // If verification already ran, don't run again
    console.log('Email verification useEffect triggered'); 
    if (hasVerified.current) return;
    
    let isCancelled = false; // Prevent race conditions
    let timer: NodeJS.Timeout | null = null;

    const verifyEmail = async () => {
      if (!token) {
        if (!isCancelled) {
          setStatus('error');
          setMessage(t('login.verificationFailed'));
        }
        return;
      }

      // Mark that verification has started
      hasVerified.current = true;

      console.log('Starting email verification for token:', token);
      try {
        const response = await fetch(
          `${import.meta.env.VITE_API_URL || 'http://localhost:3000'}/auth/verify-email/${token}`,
          {
            method: 'GET',
            headers: {
              'Content-Type': 'application/json',
            },
          }
        );

        //if (isCancelled) return; // Don't update state if cancelled

        console.log('Verification response status:', response.status);
        const data = await response.json();

        if (response.ok) {
          setStatus('success');
          setMessage(t('login.verificationSuccess'));
          
          // Start countdown
          timer = setInterval(() => {
            setCountdown((prev) => {
              if (prev <= 1) {
                if (timer) clearInterval(timer);
                navigate('/login-page');
                return 0;
              }
              return prev - 1;
            });
          }, 1000);
        } else {
          setStatus('error');
          setMessage(data.message || t('login.verificationFailed'));
        }
      } catch (error) {
        if (!isCancelled) {
          console.error('Verification error:', error);
          setStatus('error');
          setMessage(t('login.verificationFailed'));
        }
        else {
          console.log('Verification aborted due to component unmounting');
        }
      } finally {
        console.log('Verification process completed with status:', status);
      }
    };

    verifyEmail();

    // Cleanup function to prevent race conditions and clear timer
    return () => {
      isCancelled = true;
      if (timer) clearInterval(timer);
    };
  }, [token, navigate, t]);

  return (
    <div className={styles.verifyContainer}>
      <div className={styles.verifyCard}>
        {/* Icon */}
        <div className={`${styles.iconContainer} ${styles[status]}`}>
          {status === 'loading' && (
            <div className={styles.spinner}></div>
          )}
          {status === 'success' && (
            <span className={`${styles.icon} ${styles.checkmark}`}>✓</span>
          )}
          {status === 'error' && (
            <span className={styles.icon}>✕</span>
          )}
        </div>

        {/* Content */}
        <div className={styles.content}>
          <h1 className={styles.title}>
            {status === 'loading' && t('login.verifyingEmail', 'Verifying Email')}
            {status === 'success' && t('login.emailVerified', 'Email Verified!')}
            {status === 'error' && t('login.verificationError', 'Verification Failed')}
          </h1>

          <p className={styles.message}>{message}</p>
          {/* Countdown for success */}
          {status === 'success' && countdown > 0 && (
            <div className={styles.countdown}>
              {t('login.redirectingIn', 'Redirecting to login in {{count}} seconds...', { count: countdown })}
            </div>
          )}

          {/* Action Buttons */}
          <div className={styles.buttonGroup}>
            {status === 'success' && (
              <Link to="/login-page" className={`${styles.button} ${styles.primaryButton}`}>
                {t('login.goToLogin', 'Go to Login')}
              </Link>
            )}

            {status === 'error' && (
              <>
                <Link to="/login-page" className={`${styles.button} ${styles.secondaryButton}`}>
                  {t('login.backToLogin', 'Back to Login')}
                </Link>
                <Link 
                  to="/login-page?tab=signup" 
                  className={`${styles.button} ${styles.primaryButton}`}
                >
                  {t('login.signUpAgain', 'Try Signing Up Again')}
                </Link>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default VerifyEmail;
